import { useState, type ReactNode } from 'react';
import { Landmark, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface BankEntryFormValues {
  valueDate: string;
  amount: number;
  iban: string;
  reference: string;
}

export interface BankEntryFormInitial {
  valueDate?: string;
  amount?: number;
  iban?: string;
  reference?: string;
}

interface BankEntryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  header?: ReactNode;
  currencyLabel: string;
  initial?: BankEntryFormInitial;
  submitLabel: string;
  submitting: boolean;
  onSubmit: (values: BankEntryFormValues) => Promise<void> | void;
}

export function BankEntryFormDialog({
  open,
  onOpenChange,
  title,
  description,
  header,
  currencyLabel,
  initial,
  submitLabel,
  submitting,
  onSubmit,
}: BankEntryFormDialogProps) {
  const [valueDate, setValueDate] = useState(initial?.valueDate ?? '');
  const [amount, setAmount] = useState(
    initial?.amount != null ? String(initial.amount) : ''
  );
  const [iban, setIban] = useState(initial?.iban ?? '');
  const [reference, setReference] = useState(initial?.reference ?? '');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValueDate(initial?.valueDate ?? '');
      setAmount(initial?.amount != null ? String(initial.amount) : '');
      setIban(initial?.iban ?? '');
      setReference(initial?.reference ?? '');
    }
  }

  const valid =
    valueDate.length > 0 &&
    amount.length > 0 &&
    !Number.isNaN(Number(amount)) &&
    iban.trim().length > 0;

  const handleClose = () => {
    if (submitting) return;
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!valid) return;
    await onSubmit({
      valueDate,
      amount: Number(amount),
      iban: iban.trim(),
      reference: reference.trim(),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : handleClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5" />
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {header}

        <div className="pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="bank-entry-value-date">Value date</Label>
              <Input
                id="bank-entry-value-date"
                type="date"
                value={valueDate}
                onChange={(e) => setValueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bank-entry-amount">
                Amount ({currencyLabel})
              </Label>
              <Input
                id="bank-entry-amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="bank-entry-iban">Counterparty IBAN</Label>
            <Input
              id="bank-entry-iban"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="DE89 3704 0044 0532 0130 00"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="bank-entry-reference">Payment reference</Label>
            <Input
              id="bank-entry-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="ACME-SERIES-A"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || submitting}
            data-testid="submit-bank-entry"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
